import { useMemo } from "react";
import { CheckCircle2, AlertTriangle, Smartphone, Globe } from "lucide-react";

// Lightweight browser/PWA sniff — used purely to give the driver a context-aware
// hint about whether their setup supports reliable web push. We never use this
// for gating behavior, only for UX guidance.
function detectEnv() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { kind: "unknown" };
  }
  const ua = navigator.userAgent || "";
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge;
  const isFirefox = /Firefox\//.test(ua);
  const isSafari = /Safari\//.test(ua) && !isChrome && !isEdge && !isFirefox;
  return { isStandalone, isIOS, isAndroid, isEdge, isChrome, isFirefox, isSafari, kind: "browser" };
}

export default function PushReliabilityBanner() {
  const env = useMemo(detectEnv, []);

  if (env.kind === "unknown") return null;

  // PWA-installed: best case, just confirm
  if (env.isStandalone) {
    return (
      <div
        className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-start gap-2.5"
        data-testid="push-banner-installed"
      >
        <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
        <div className="text-xs leading-relaxed text-emerald-100/90">
          <span className="font-semibold text-emerald-300">Installed to your home screen.</span>{" "}
          Push notifications will work reliably even when Link Ride is closed.
        </div>
      </div>
    );
  }

  // iOS Safari (not installed) — push only works once "Add to Home Screen"
  if (env.isIOS) {
    return (
      <div
        className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2.5"
        data-testid="push-banner-ios"
      >
        <Smartphone className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="text-xs leading-relaxed text-amber-100/90">
          <span className="font-semibold text-amber-300">iPhone tip:</span> push won't fire from a
          regular Safari tab. Tap the <b>Share</b> icon at the bottom of Safari →{" "}
          <b>Add to Home Screen</b>, then open Link Ride from the home-screen icon to receive ride alerts.
        </div>
      </div>
    );
  }

  // Edge — Microsoft's push backend is flaky
  if (env.isEdge) {
    return (
      <div
        className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2.5"
        data-testid="push-banner-edge"
      >
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="text-xs leading-relaxed text-amber-100/90">
          <span className="font-semibold text-amber-300">Edge is unreliable for push.</span>{" "}
          For dependable ride alerts use <b>Google Chrome</b> (desktop or Android), or install Link Ride
          as a PWA (browser menu → "Install Link Ride").
        </div>
      </div>
    );
  }

  // Chrome / Android / Firefox — good case
  if (env.isChrome || env.isAndroid || env.isFirefox) {
    return (
      <div
        className="rounded-xl border border-white/10 bg-white/[0.03] p-3 flex items-start gap-2.5"
        data-testid="push-banner-good"
      >
        <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
        <div className="text-xs leading-relaxed text-zinc-300">
          Push notifications work great in {env.isAndroid ? "Chrome on Android" : "this browser"}.
          For the most reliable delivery, install Link Ride to your home screen (browser menu →
          "Install Link Ride" or "Add to Home Screen").
        </div>
      </div>
    );
  }

  // Default catch-all
  return (
    <div
      className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2.5"
      data-testid="push-banner-default"
    >
      <Globe className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
      <div className="text-xs leading-relaxed text-amber-100/90">
        For the most reliable ride-alert push notifications, use <b>Google Chrome</b> or install
        Link Ride to your home screen.
      </div>
    </div>
  );
}
